<template>
  <Sheet v-model:open="isOpen">
    <SheetContent class="overflow-y-auto">
      <SheetHeader padded>
        <SheetTitle size="lg">Refer an agent</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex flex-col gap-4">
        <p class="text-sm text-muted-foreground">
          We'll let your team know you're vouching for this agent. They'll be added to the recruiting pipeline tagged as
          a manual referral from you.
        </p>

        <Field class="gap-1">
          <FieldLabel for="firstName">First name</FieldLabel>
          <Input id="firstName" v-model="form.first_name" placeholder="Jane" />
        </Field>

        <Field class="gap-1">
          <FieldLabel for="lastName">Last name</FieldLabel>
          <Input id="lastName" v-model="form.last_name" placeholder="Doe" />
        </Field>

        <Field class="gap-1">
          <FieldLabel for="email">Email</FieldLabel>
          <Input id="email" v-model="form.email" type="email" placeholder="jane@example.com" />
        </Field>

        <PhoneInput
          label="Phone number"
          placeholder="+1 (281) 330 8004"
          :model-value="form.phone_number"
          @update:model-value="form.phone_number = $event"
        />

        <Field class="gap-1">
          <FieldLabel>License status</FieldLabel>
          <Select v-model="form.license_status">
            <SelectTrigger>
              <SelectValue placeholder="Select license status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="licensed">Licensed</SelectItem>
              <SelectItem value="license_in_progress">License in progress</SelectItem>
              <SelectItem value="unlicensed">Unlicensed</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field class="gap-1">
          <FieldLabel for="notes"> Notes <span class="text-xs text-muted-foreground">(optional)</span> </FieldLabel>
          <Textarea
            id="notes"
            v-model="form.notes"
            placeholder="e.g. Met them at a recent closing — great producer, would be a strong fit for our team."
            rows="4"
          />
        </Field>
      </SheetBody>

      <SheetFooter bordered class="flex-row justify-end gap-3">
        <SheetClose as-child>
          <Button variant="outline" :disabled="isSaving">Cancel</Button>
        </SheetClose>
        <Button :disabled="isSaving" @click="submit">
          {{ isSaving ? 'Submitting...' : 'Refer agent' }}
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import PhoneInput from '@/components/shared/PhoneInput.vue';
import { Api } from '@/api';
import type { CreateAgentReferralPayload } from '@/api/agent/referrals';
import { toast } from 'vue-sonner';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{
  'update:open': [value: boolean];
  created: [];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});

type LicenseStatus = CreateAgentReferralPayload['license_status'];

const defaultForm = () => ({
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  license_status: '' as LicenseStatus | '',
  notes: '',
});

const form = ref(defaultForm());
const isSaving = ref(false);

watch(
  () => props.open,
  open => {
    if (open) form.value = defaultForm();
  }
);

const validationErrors = () => {
  const errors: string[] = [];
  if (form.value.first_name.trim() === '') errors.push('First name is required');
  if (form.value.last_name.trim() === '') errors.push('Last name is required');
  if (form.value.email.trim() === '') errors.push('Email is required');
  if (form.value.phone_number.trim() === '') errors.push('Phone number is required');
  if (form.value.license_status === '') errors.push('License status is required');
  return errors;
};

const submit = async () => {
  const errors = validationErrors();
  if (errors.length) {
    toast.error('Could not submit referral', { description: errors.join(', ') });
    return;
  }

  isSaving.value = true;
  try {
    const response = await Api.AgentReferrals.createReferral({
      first_name: form.value.first_name.trim(),
      last_name: form.value.last_name.trim(),
      email: form.value.email.trim(),
      phone_number: form.value.phone_number.trim(),
      license_status: form.value.license_status as LicenseStatus,
      notes: form.value.notes.trim() || undefined,
    });

    if (response.status === 'success') {
      toast.success('Referral submitted', {
        description: `We've added ${form.value.first_name} ${form.value.last_name} to the recruiting pipeline.`,
      });
      emit('created');
      isOpen.value = false;
    } else {
      toast.error('Could not submit referral', {
        description: response.messages.join(', '),
      });
    }
  } catch {
    toast.error('Could not submit referral', {
      description: 'An unexpected error occurred.',
    });
  } finally {
    isSaving.value = false;
  }
};
</script>
